import { useEffect, useState } from "react";
import api from "@/utils/api";
import { AlertTriangle, FileText, ShieldAlert, ClipboardList, Building, Info } from "lucide-react";
import { fmtDate } from "@/components/governance/govUtils";

const Stat = ({ label, value, icon: Icon, tone = "slate", testId, onClick }) => (
  <button type="button" onClick={onClick} className="text-left bg-white border border-border rounded-md p-4 hover:border-slate-300 transition-colors" data-testid={testId}>
    <div className="flex items-center justify-between">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <Icon size={15} className={tone === "red" ? "text-red-500" : tone === "amber" ? "text-amber-500" : "text-slate-400"} />
    </div>
    <p className={`mt-1 text-2xl font-heading font-semibold ${tone === "red" && value ? "text-red-600" : tone === "amber" && value ? "text-amber-700" : "text-slate-900"}`}>{value}</p>
  </button>
);

export const GovOverview = ({ onNavigate }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/governance/overview").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-5" data-testid="gov-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Drafts awaiting review" value={d.documents_draft + d.documents_in_review} icon={FileText} tone="amber" testId="gov-stat-drafts" onClick={() => onNavigate("documents")} />
        <Stat label="Needing legal review" value={d.documents_legal_review} icon={FileText} tone="amber" testId="gov-stat-legal" onClick={() => onNavigate("documents")} />
        <Stat label="Missing signed agreements" value={d.missing_agreements.length} icon={ClipboardList} tone="red" testId="gov-stat-missing" onClick={() => onNavigate("acceptances")} />
        <Stat label="Open DPIAs" value={d.dpia_open} icon={ShieldAlert} tone="amber" testId="gov-stat-dpia" onClick={() => onNavigate("dpia")} />
        <Stat label="High residual risks" value={d.risks_open_high} icon={AlertTriangle} tone="red" testId="gov-stat-risks" onClick={() => onNavigate("dpia")} />
        <Stat label="Open incidents" value={d.incidents_open} icon={AlertTriangle} tone="red" testId="gov-stat-incidents" onClick={() => onNavigate("incidents")} />
        <Stat label="Suppliers needing review" value={d.subprocessors_unreviewed} icon={Building} tone="amber" testId="gov-stat-suppliers" onClick={() => onNavigate("subprocessors")} />
        <Stat label="Controls with evidence gaps" value={d.evidence_gaps} icon={ClipboardList} tone="amber" testId="gov-stat-gaps" onClick={() => onNavigate("controls")} />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-border rounded-md p-4">
          <h3 className="font-heading font-semibold text-sm mb-2">Control status breakdown</h3>
          <ul className="space-y-1 text-sm" data-testid="gov-controls-breakdown">
            {Object.entries(d.controls_by_status).map(([k, v]) => (
              <li key={k} className="flex justify-between"><span className="text-slate-600">{k}</span><span className="font-semibold">{v}</span></li>
            ))}
          </ul>
        </div>
        <div className="bg-white border border-border rounded-md p-4">
          <h3 className="font-heading font-semibold text-sm mb-2">Needs attention</h3>
          <ul className="space-y-1.5 text-sm text-slate-700">
            {d.missing_agreements.map((t) => <li key={t}>• No signed <strong>{t.toUpperCase()}</strong> on record</li>)}
            {d.unpublished_customer_docs.map((t) => <li key={t}>• <strong>{t.replace("_", " ")}</strong> not yet approved and published</li>)}
            {d.incidents_overdue > 0 && <li className="text-red-700">• {d.incidents_overdue} incident action(s) overdue</li>}
            {d.reviews_due.map((r) => <li key={r.id}>• Review due {fmtDate(r.review_date)}: {r.title}</li>)}
          </ul>
        </div>
      </div>
      <p className="flex items-start gap-2 text-xs text-muted-foreground" data-testid="gov-scoring-note"><Info size={13} className="mt-0.5 shrink-0" />{d.scoring_note}</p>
    </div>
  );
};
